<template>
    <div>
 
      <!-- Textarea for input -->
      <textarea
        type="text"
        class="form-control"
        v-model="inputText"
        :placeholder="placeholder"
        style="margin: 16px auto;"
        @change="handleTextChange"
      ></textarea>
  
      <!-- Buttons for Spell Check and Use AI -->
      <div style="margin: auto; justify-self: center; text-align: center; display: flex; gap: 8px; flex-wrap: wrap; justify-content: center;">
        <n-button @click="spellCheck" type="primary">Spell Check</n-button>
        <n-button @click="useAI" type="info" style="margin-left: 10px;">Use AI to help compose your Scope Of Services</n-button>
      </div>
  
      <!-- Naive UI Modal for AI Suggestions -->
      <n-modal v-model:show="showModal">
        <n-card
          style="max-width: 800px; width: 80vw;"
          :title="modalTitle"
          :bordered="false"
          size="medium"
          role="dialog"
          aria-modal="true"
          closable @close="cancelAISuggestion"
        >
        
          <div id="content-wrapper">
            <div class="bold">From:</div>
            <div class="info" v-html="originalContent"></div>
            <div class="bold">To:</div>
            <div class="info" v-html="aiSuggestedContent"></div>
          </div>
  
          <template #footer >
            <n-button @click="cancelAISuggestion" style="margin-left: 10px;float:right;">Cancel</n-button>
            <n-button @click="applyAISuggestion" type="primary" style="float:right;">{{btnAcceptText}}</n-button>
           
          </template>
        </n-card>
      </n-modal>
    </div>
  </template>
  
  <script>
  import { ref, watch } from "vue";
  import { NButton, NModal, NCard, c } from "naive-ui";

  import useAi from "../composables/useAi";
  
  export default {
    components: {
      NButton,
      NModal,
      NCard,
    },
    props: {
      // Props for customization
      placeholder: {
        type: String,
        default: "Enter text here",
      },
      initialText: {
        type: String,
        default: "",
      },
    },

    emits: ["update:text"], // Defining the emit event

    setup(props, { emit }) {

      const ai = useAi();

      const modalTitle = ref("AI Suggest you to use the following Scope Of Services description");
      const inputText = ref(props.initialText);
      const showModal = ref(false);
      const originalContent = ref("This is the original content.");
      const aiSuggestedContent = ref("This is the AI suggested content.");

      const btnAcceptText = ref("Use AI Suggestion");

      watch(
        () => props.initialText,
        (newValue) => {
          //console.log("initialText changed:", newValue);
          inputText.value = newValue;
        },
        { immediate: true } // Ensures pre-filling on mount
      );

      const initData = (text) => {
        if(!text) return;
        inputText.value = text;
        emit("update:text", inputText.value);
      }
      const handleTextChange = () => {
        console.log("handleTextChange called");
        emit("update:text", inputText.value); // Emit updated text to parent
      };
  
      // Spell Check Functionality
      const spellCheck = async () => {
        const text = inputText.value;
        originalContent.value = text.replace(/\n/g, "<br>");

        if(originalContent.value == "") return;

        emit("showLoader");
        try {
            const suggestion = await ai.spellCheck(text);

            emit("hideLoader");

            if(!suggestion) return;

            modalTitle.value = "Spell check suggests the following changes";
            btnAcceptText.value = "Accept Suggestions";

            const aiText = cleanAiText(suggestion);

            aiSuggestedContent.value = aiText.trim().replace(/\n/g, "<br>");
            showModal.value = true; // Show the modal with AI suggestions
        } catch (error) {
            emit("hideLoader");
            console.error("Error calling AI service:", error);
        }
      };


      const cleanAiText = (text) => {
        console.log("cleanAiText called",text);
        if(!text) return '';
        text = text.trim();
        let firstParagraph = text.split("\n\n");

        if(firstParagraph && 
          (
            firstParagraph[0].startsWith("Here is the") || firstParagraph[0].startsWith("Here's the") 
            || firstParagraph[0].indexOf("improved text") > -1  || firstParagraph[0].indexOf("improved version")  > -1
          )
        ){

          console.log("found improved text", text);

          let index = text.indexOf("\n");
          let cleanText = text.substring(index + 1, text.length).trim();

          console.log("substring", cleanText);

          let paragaphs = cleanText.split("\n");
          if(paragaphs.length > 1){
              cleanText = '';

              paragaphs.forEach((paragraph, index) => {
                console.log("paragraph", paragraph);
                if(paragraph != "```" && paragraph != "```html"){
                  cleanText += paragraph;
                }
                      
                cleanText += '\n';
              });
             
          }

          cleanText = cleanText.trim();
          if(cleanText.startsWith("\"") && cleanText.endsWith("\"")) 
            cleanText = cleanText.substring(1, cleanText.length - 1);


          return cleanText;
        }else{

          if(text.startsWith("\"") && text.endsWith("\"")) 
            text = text.substring(1, text.length - 1);
          return text;
        }
      };
  
      // Use AI Functionality
      const useAI = async () => {
        const text = inputText.value;
        originalContent.value = text.replace(/\n/g, "<br>");

        if(originalContent.value == "") return;

        emit("showLoader");
        try {
            const suggestion = await ai.improve(text);

            emit("hideLoader");

            if(!suggestion) return;

            modalTitle.value = "AI Suggest the following grammatical changes to your Scope Of Services";
            btnAcceptText.value = "Use AI Suggestion";

            const aiText = cleanAiText(suggestion);

            aiSuggestedContent.value = aiText.trim().replace(/\n/g, "<br>");
            showModal.value = true; // Show the modal with AI suggestions
        } catch (error) {
            emit("hideLoader");
            console.error("Error calling AI service:", error);
        }
      };
  
      // Apply AI Suggestion
      const applyAISuggestion = () => {
        const textWithoutHtml = aiSuggestedContent.value.replace(/<br\s*\/?>/g, "\n").replace(/<[^>]*>/g, "");

        // Update inputText with the cleaned content
        inputText.value = textWithoutHtml;
        showModal.value = false;
        emit("update:text", inputText.value); // Emit updated text to parent
      };
  
      // Cancel AI Suggestion
      const cancelAISuggestion = () => {
        showModal.value = false;
      };
  
      return {
        inputText,
        showModal,
        originalContent,
        aiSuggestedContent,
        spellCheck,
        useAI,
        applyAISuggestion,
        cancelAISuggestion,
        modalTitle,
        btnAcceptText,
        handleTextChange,
        initData
      };
    },
  };
  </script>
  
  <style scoped>
  /* Add your custom styles here */

  #content-wrapper {
    max-height: 80vh; /* Set a fixed height */
    overflow-y: auto; /* Enable vertical scrolling */
    padding: 10px;
  }

  /* Optional: Improve scrolling experience */
  #content-wrapper::-webkit-scrollbar {
    width: 6px;
  }

  #content-wrapper::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 3px;
  }

  .info{ font-size: 12px; margin: 12px 0px;}

  </style>